/** Non-translatable tutorial step wiring (routes, gates, selectors). */
export interface TutorialStepLayout {
  id: number;
  route: string;
  highlightSelector: string;
  highlightSelectors?: string[];
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  gateType: 'manual' | 'auto';
  gateKey?: string;
  minValue?: number;
  hasTip?: boolean;
  skipNavigateOnEnter?: boolean;
  popupAnchorSelector?: string;
  /** When true, the step is only used by a My Training module tour. */
  moduleOnly?: boolean;
}

export const TUTORIAL_STEP_LAYOUT: TutorialStepLayout[] = [
  {
    id: 0,
    route: '/',
    highlightSelector: '',
    position: 'center',
    gateType: 'manual',
  },
  {
    id: 1,
    route: '/new-site',
    highlightSelector: '[data-tutorial="map-container"]',
    highlightSelectors: [
      // tutorial-wheel-connections lives on the Hub, not /new-site -- dropped (RCA 2026-06-15).
      '[data-tutorial="customer-type-sidebar"]',
      '[data-tutorial="map-container"]',
    ],
    position: 'left',
    gateType: 'auto',
    gateKey: 'connectionCount',
    minValue: 15,
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 2,
    route: '/new-site',
    highlightSelector: '[data-tutorial="download-survey"]',
    position: 'bottom',
    gateType: 'auto',
    gateKey: 'surveyDownloaded',
    hasTip: true,
  },
  {
    id: 3,
    route: '/new-site',
    highlightSelector: '',
    highlightSelectors: [
      '[data-tutorial="nav-design-grid"]',
      '[data-tutorial="upload-file"]',
    ],
    position: 'right',
    popupAnchorSelector: '[data-tutorial="upload-file"]',
    gateType: 'auto',
    gateKey: 'connectionsUploaded',
    skipNavigateOnEnter: true,
  },
  {
    id: 4,
    route: '/exclusions',
    highlightSelector: '',
    highlightSelectors: [
      '[data-tutorial="tutorial-nav-exclusions"]',
      '[data-tutorial="fetch-osm"]',
    ],
    position: 'bottom',
    gateType: 'auto',
    gateKey: 'exclusionsDefined',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 5,
    route: '/planner',
    // PlatformNav (and its tutorial-top-nav-track-construction anchor) renders
    // null on /planner, so highlight the in-planner Save Project button only (RCA 2026-06-15).
    highlightSelector: '[data-tutorial="save-project"]',
    highlightSelectors: [
      '[data-tutorial="save-project"]',
    ],
    position: 'left',
    gateType: 'auto',
    gateKey: 'projectSaved',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 6,
    route: '/planner',
    highlightSelector: '',
    highlightSelectors: [
      '[data-tutorial="view-mode-vdrop"]',
      '[data-tutorial="vdrop-scenario"]',
    ],
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 22,
    route: '/planner',
    highlightSelector: '[data-tutorial="highres-basemap-panel"]',
    highlightSelectors: [
      '[data-tutorial="highres-basemap-panel"]',
    ],
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 7,
    route: '/design-grid',
    highlightSelector: '',
    highlightSelectors: [
      // tutorial-top-nav-design-grid not rendered on this route -- dropped (RCA 2026-06-15).
      '[data-tutorial="tutorial-nav-return-design-grid"]',
      '[data-tutorial="run-design"]',
      '[data-tutorial="ugridnet-build-choice"]',
    ],
    position: 'left',
    gateType: 'auto',
    gateKey: 'networkDesigned',
    skipNavigateOnEnter: true,
  },
  {
    id: 8,
    route: '/design-plant',
    highlightSelector: '',
    highlightSelectors: [
      // tutorial-top-nav-design-plant not rendered on this route -- dropped (RCA 2026-06-15).
      '[data-tutorial="fetch-tmy"]',
    ],
    position: 'bottom',
    gateType: 'auto',
    gateKey: 'tmyFetched',
    skipNavigateOnEnter: true,
  },
  {
    id: 9,
    route: '/design-plant',
    highlightSelector: '',
    highlightSelectors: [
      '[data-tutorial="tutorial-open-8760-tool"]',
      '[data-tutorial="build-8760"]',
    ],
    position: 'bottom',
    gateType: 'auto',
    gateKey: 'demandProfiled',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 10,
    route: '/design-plant',
    highlightSelector: '',
    highlightSelectors: [
      '[data-tutorial="pv-yield-panel"]',
      '[data-tutorial="run-pv-sim"]',
    ],
    position: 'bottom',
    gateType: 'auto',
    gateKey: 'pvSimulated',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 11,
    route: '/design-plant',
    highlightSelector: '[data-tutorial="run-optimization"]',
    highlightSelectors: [
      '[data-tutorial="include-mv-losses"]',
      '[data-tutorial="run-optimization"]',
    ],
    position: 'left',
    gateType: 'auto',
    gateKey: 'sizingComplete',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 12,
    route: '/predict',
    highlightSelector: '',
    highlightSelectors: [
      // PlatformNav is mounted on /design-plant (where skipNavigateOnEnter
      // leaves the user) and on /predict — not only on /planner.
      '[data-tutorial="tutorial-top-nav-predict"]',
      '[data-tutorial="run-prediction"]',
    ],
    position: 'left',
    gateType: 'auto',
    gateKey: 'predictionRun',
    skipNavigateOnEnter: true,
  },
  {
    id: 16,
    route: '/gantt',
    highlightSelector: '[data-tutorial="gantt-project-picker"]',
    highlightSelectors: [
      '[data-tutorial="tutorial-top-nav-gantt"]',
      '[data-tutorial="gantt-project-picker"]',
    ],
    position: 'bottom',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 13,
    route: '/topology-compare',
    highlightSelector: '[data-tutorial="topology-mode-selector"]',
    position: 'bottom',
    gateType: 'manual',
    hasTip: true,
  },
  {
    id: 15,
    route: '/topology-compare',
    highlightSelector: '[data-tutorial="topology-workflow-guide"]',
    position: 'bottom',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
  },
  {
    id: 14,
    route: '/',
    highlightSelector: '',
    position: 'center',
    gateType: 'manual',
    skipNavigateOnEnter: true,
  },
  // Voltage-drop training module only (not part of the full platform tour).
  {
    id: 17,
    route: '/planner',
    highlightSelector: '[data-tutorial="view-mode-vdrop"]',
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    moduleOnly: true,
  },
  {
    id: 18,
    route: '/planner',
    highlightSelector: '[data-tutorial="vdrop-scenario"]',
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
    moduleOnly: true,
  },
  {
    id: 19,
    route: '/planner',
    highlightSelector: '[data-tutorial="vdrop-compare"]',
    highlightSelectors: [
      '[data-tutorial="vdrop-compare"]',
      '[data-tutorial="vdrop-compare-panel"]',
    ],
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
    moduleOnly: true,
  },
  {
    id: 20,
    route: '/planner',
    highlightSelector: '',
    highlightSelectors: [
      '[data-tutorial="vdrop-data-quality"]',
      '[data-tutorial="vdrop-autosize"]',
    ],
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
    moduleOnly: true,
  },
  {
    id: 21,
    route: '/planner',
    highlightSelector: '[data-tutorial="vdrop-load-a"]',
    highlightSelectors: [
      '[data-tutorial="vdrop-load-a"]',
      '[data-tutorial="view-mode-vdrop"]',
    ],
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
    moduleOnly: true,
  },
  {
    id: 23,
    route: '/design-plant',
    highlightSelector: '[data-tutorial="as-built-sld"]',
    highlightSelectors: ['[data-tutorial="as-built-sld"]'],
    position: 'top',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
    moduleOnly: true,
  },
  {
    id: 24,
    route: '/planner',
    highlightSelector: '[data-tutorial="auto-add-ptbs"]',
    highlightSelectors: [
      '[data-tutorial="auto-add-ptbs"]',
      '[data-tutorial="export-bom-excel"]',
    ],
    position: 'left',
    gateType: 'manual',
    hasTip: true,
    skipNavigateOnEnter: true,
    moduleOnly: true,
  },
];

export const TUTORIAL_SAVE_STEP_INDEX = TUTORIAL_STEP_LAYOUT.findIndex((s) => s.gateKey === 'projectSaved');
export const TUTORIAL_RUN_DESIGN_STEP_INDEX = TUTORIAL_STEP_LAYOUT.findIndex((s) => s.gateKey === 'networkDesigned');
